import { localizeError, apiErrorSchema } from '@wx-weiqi/contracts';

import { secureRandomUuid } from './random-id';

export interface ResponseSchema<Response> {
  parse(value: unknown): Response;
}

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly accessToken?: string;
  readonly requestId?: string;
  readonly body?: unknown;
  readonly formData?: FormData;
}

export interface DownloadedFile {
  readonly blob: Blob;
  readonly filename: string;
}

export class WebApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly syncRequired = false,
  ) {
    super(localizeError(code, message));
    this.name = 'WebApiError';
  }
}

export class HttpService {
  private readonly baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/+$/u, '');
  }

  async request<Response>(
    path: string,
    schema: ResponseSchema<Response>,
    options: RequestOptions = {},
  ): Promise<Response> {
    const headers = new Headers({ Accept: 'application/json' });
    if (options.body !== undefined && options.formData !== undefined) {
      throw new TypeError('A request cannot contain JSON and multipart bodies together');
    }
    if (options.body !== undefined) headers.set('Content-Type', 'application/json');
    if (options.accessToken !== undefined) {
      headers.set('Authorization', `Bearer ${options.accessToken}`);
    }
    if (options.requestId !== undefined) headers.set('Idempotency-Key', options.requestId);

    let response: globalThis.Response;
    try {
      response = await fetch(`${this.baseUrl}${path}`, {
        method: options.method ?? 'GET',
        headers,
        ...(options.formData !== undefined
          ? { body: options.formData }
          : options.body === undefined
            ? {}
            : { body: JSON.stringify(options.body) }),
      });
    } catch {
      throw new WebApiError('NETWORK_UNAVAILABLE', '无法连接服务器，请检查网络后重试。', 0);
    }

    const value: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const parsed = apiErrorSchema.safeParse(value);
      if (parsed.success) {
        throw new WebApiError(
          parsed.data.code,
          parsed.data.message,
          response.status,
          parsed.data.syncRequired === true,
        );
      }
      throw new WebApiError(
        response.status === 413 ? 'INVALID_PAYLOAD' : 'UNEXPECTED_RESPONSE',
        response.status === 413
          ? '上传请求超过服务器限制。选页仅限制识别范围，仍需上传原文件；请核对文件大小或联系管理员调整上传限额。'
          : `服务器返回错误（${response.status}）。`,
        response.status,
      );
    }

    try {
      return schema.parse(value);
    } catch {
      throw new WebApiError('INVALID_SERVER_RESPONSE', '服务器响应未通过契约校验。', 502);
    }
  }

  async download(
    path: string,
    fallbackFilename: string,
    options: Omit<RequestOptions, 'body' | 'method'> = {},
  ): Promise<DownloadedFile> {
    const headers = new Headers({ Accept: 'application/x-go-sgf, application/octet-stream' });
    if (options.accessToken !== undefined) {
      headers.set('Authorization', `Bearer ${options.accessToken}`);
    }
    if (options.requestId !== undefined) headers.set('Idempotency-Key', options.requestId);

    let response: globalThis.Response;
    try {
      response = await fetch(`${this.baseUrl}${path}`, { method: 'GET', headers });
    } catch {
      throw new WebApiError('NETWORK_UNAVAILABLE', '无法连接服务器，请检查网络后重试。', 0);
    }
    if (!response.ok) {
      const value: unknown = await response.json().catch(() => null);
      const parsed = apiErrorSchema.safeParse(value);
      if (parsed.success) {
        throw new WebApiError(
          parsed.data.code,
          parsed.data.message,
          response.status,
          parsed.data.syncRequired === true,
        );
      }
      throw new WebApiError(
        response.status === 413 ? 'INVALID_PAYLOAD' : 'UNEXPECTED_RESPONSE',
        response.status === 413
          ? '上传请求超过服务器限制。选页仅限制识别范围，仍需上传原文件；请核对文件大小或联系管理员调整上传限额。'
          : `服务器返回错误（${response.status}）。`,
        response.status,
      );
    }
    return Object.freeze({
      blob: await response.blob(),
      filename: safeDownloadFilename(response.headers.get('Content-Disposition'), fallbackFilename),
    });
  }
}

export function safeDownloadFilename(disposition: string | null, fallback: string): string {
  const extended = /filename\*=UTF-8''([^;]+)/iu.exec(disposition ?? '')?.[1];
  const basic = /filename=(?:"([^"]+)"|([^;]+))/iu.exec(disposition ?? '');
  let candidate = extended ?? basic?.[1] ?? basic?.[2] ?? fallback;
  try {
    candidate = decodeURIComponent(candidate.trim());
  } catch {
    candidate = fallback;
  }
  const leaf = candidate.split(/[\\/]/u).at(-1) ?? fallback;
  const sanitized = [...leaf]
    .map((character) => {
      const code = character.codePointAt(0) ?? 0;
      return code < 32 || code === 127 ? '_' : character;
    })
    .join('')
    .replace(/[<>:"|?*]/gu, '_')
    .trim()
    .slice(0, 160);
  return sanitized.toLowerCase().endsWith('.sgf') && sanitized.length > 4 ? sanitized : fallback;
}

export function nextRequestId(prefix: string): string {
  const suffix = secureRandomUuid();
  const maxPrefixLength = 64 - suffix.length - 1;
  const boundedPrefix = prefix.trim().slice(0, maxPrefixLength) || 'web';
  return `${boundedPrefix}-${suffix}`;
}
